import { FloatingLabelProps, FloatingLabel, Form } from 'react-bootstrap';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { NumericFormat } from 'react-number-format';
import { removeNumbers } from './FormHelpers';
import { FormattedMessageDiv, useWizardFormContext } from '@odmonorepo/core-components';
import { trackField } from 'merchant-components';
import { ReactNode } from 'react';

interface FormInputProps<TFieldValues extends FieldValues> extends Omit<FloatingLabelProps, 'label'> {
  name: Path<TFieldValues>;
  required?: boolean;
  type?: string;
  disabled?: boolean;
  currency?: string;
  useYupErrors?: boolean;
  allowCents?: boolean;
  addComma?: boolean;
  helperText?: ReactNode;
}
export const FormMoneyInput = <TFieldValues extends FieldValues>({name, required = true, type = 'number', currency = '$', disabled = false, useYupErrors = false, allowCents = false, addComma = true, helperText, ...props}: FormInputProps<TFieldValues>) => {
  const { register, watch, clearErrors, setValue, trigger, formState } = useFormContext() || { register: () => {}, formState: {} as any};
  const { formName } = useWizardFormContext();
  const { onChange: registeredOnChange, onBlur: registeredOnBlur, ...restRegister } = register(name);
  const currentValue = watch(name);
  const hasError = !!formState?.errors?.[name];
  const fieldError = name?.split('.').reduce((o: any, k: string) => (o || {})[k], formState?.errors) as any;
  return (
    <FloatingLabel controlId={name} {...props} label={<FormattedMessageDiv id={'formInput.' + removeNumbers(name)} values={{ sup: (text: string) => <sup>{text}</sup> }} />}>
      <span className={`currency-symbol ${disabled && 'text-disabled'}`}>{currency}</span>
      <NumericFormat
        id={String(name)}
        className={`form-control ps-4 ${hasError ? 'is-invalid' : ''}`}
        onWheel={e => e.currentTarget.blur()}
        thousandSeparator={addComma}
        value={currentValue || ''}
        onValueChange={(v) => {
          clearErrors(name);
          // Remove commas from the formatted value and convert to number
          const cleanValue = v.value.replace(/,/g, '');
          const numericValue = cleanValue ? Number(cleanValue) : undefined;
         setValue(name, numericValue as any);
          trigger(name);
        }}
        isAllowed={(values) => {
          if (values.value === '') return true;
          if (!allowCents && values.value.includes('.')) return false;
          if (allowCents) return true;
          return values.floatValue === undefined || Number.isInteger(values.floatValue);
        }}
        onBlur={(e) => {
          trackField(
            formName,
            name as string,
            'Blur'
          );
          if (registeredOnBlur) {
            registeredOnBlur(e);
          }
        }}
        disabled={disabled}
        name={name}
        inputMode={allowCents ? 'decimal' : 'numeric'}
       />
      <style>{`.currency-symbol { position: absolute; left: .5rem; top: 1.55rem;`}</style>
      {helperText && (
        <div className="text-gray fs-8 mt-2">
          {typeof helperText === 'boolean' ? <FormattedMessage id={'formInput.' + removeNumbers(name) + '.helperText'} /> : helperText}
        </div>
      )}
      <Form.Control.Feedback type="invalid">
        {useYupErrors && fieldError?.message
          ? (
              <FormattedMessage
                id={fieldError.message}
                defaultMessage={fieldError.message}
              />
          ) : (
              <FormattedMessage id={'formInput.' + removeNumbers(name) + '.error'} />
          )
        }
      </Form.Control.Feedback>
    </FloatingLabel>
  );
};

export default FormMoneyInput;
